import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { bgnToEur } from '../../domain/currency';
import { listingInYearRanges, rankListingsByDeal, type DealVerdict, type LiveListing, type YearRange } from '../../domain/listings';
import type { YearPriceStats } from '../../domain/pricing';
import { LISTINGS_API_URL, useLiveListings } from '../../hooks/useLiveListings';
import type { GenerationFilter } from './GenerationPicker';

const INITIAL_VISIBLE = 10;
const THUMB_SIZE = { width: { xs: 88, sm: 112 }, height: { xs: 66, sm: 84 } };

const FUEL_LABELS: Record<string, string> = {
  petrol: 'Petrol',
  diesel: 'Diesel',
  hybrid: 'Hybrid',
  electric: 'Electric',
  lpg: 'LPG',
  cng: 'CNG',
};

function formatEur(bgn: number): string {
  return `${Math.round(bgnToEur(bgn)).toLocaleString()} €`;
}

function minutesAgo(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  return minutes < 1 ? 'just now' : `${minutes} min ago`;
}

function DealChip({ verdict }: { verdict: DealVerdict | null }) {
  if (!verdict) {
    return null;
  }

  const percent = Math.abs(Math.round(verdict.percentVsMarket));
  const label =
    verdict.tone === 'good' ? `${percent}% below market` : verdict.tone === 'high' ? `${percent}% above market` : 'Fair price';
  const color = verdict.tone === 'good' ? 'success' : verdict.tone === 'high' ? 'error' : 'default';
  const title = `Median for this year: ${formatEur(verdict.marketMedianBgn)} across ${verdict.sampleSize} ads`;

  return <Chip size="small" color={color} label={label} title={title} />;
}

/** The ad's own photo, falling back to our model photo, then to an icon if neither loads. */
function ListingThumb({ sources, alt }: { sources: string[]; alt: string }) {
  const [failed, setFailed] = useState(0);
  const src = sources[failed];

  return (
    <Box
      sx={{
        ...THUMB_SIZE,
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed((n) => n + 1)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <DirectionsCarIcon color="disabled" />
      )}
    </Box>
  );
}

interface ListingRowProps {
  listing: LiveListing;
  verdict: DealVerdict | null;
  fallbackImageUrl: string | null;
}

function ListingRow({ listing, verdict, fallbackImageUrl }: ListingRowProps) {
  const details = [
    listing.year,
    listing.mileageKm !== null ? `${listing.mileageKm.toLocaleString()} km` : null,
    listing.powerHp !== null ? `${listing.powerHp} hp` : null,
    listing.fuel ? FUEL_LABELS[listing.fuel] ?? listing.fuel : null,
    listing.gearbox === 'automatic' ? 'Automatic' : listing.gearbox === 'manual' ? 'Manual' : null,
    listing.location,
  ].filter(Boolean);

  const price = (
    <Typography variant="h6" component="span" sx={{ whiteSpace: 'nowrap', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
      {formatEur(listing.priceBgn)}
    </Typography>
  );

  return (
    <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} sx={{ py: 1.5 }} alignItems="center">
      <Link href={listing.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', flexShrink: 0 }}>
        <ListingThumb
          sources={[listing.imageUrl, fallbackImageUrl].filter((url): url is string => Boolean(url))}
          alt={listing.title}
        />
      </Link>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Link href={listing.url} target="_blank" rel="noopener noreferrer" underline="hover" fontWeight={600}>
          {listing.title}
        </Link>
        <Typography variant="body2" color="text.secondary">
          {details.join(' · ')}
        </Typography>
        {/* On phones the price and badge sit under the details instead of in their own column. */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: 'flex', sm: 'none' }, mt: 0.5 }}>
          {price}
          <DealChip verdict={verdict} />
        </Stack>
      </Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' }, flexShrink: 0 }}>
        <DealChip verdict={verdict} />
        <Box sx={{ minWidth: 100, textAlign: 'right' }}>{price}</Box>
      </Stack>
    </Stack>
  );
}

interface LiveListingsProps {
  make: string;
  model: string;
  /** Median asking price per production year from the latest weekly snapshot. */
  yearPrices: Map<number, YearPriceStats> | null;
  /** Our own photo of the model, shown for ads without a usable thumbnail. */
  fallbackImageUrl: string | null;
  /** The model's generations and the ones the buyer picked; only ads from the picked generations' years show. */
  generationFilters?: GenerationFilter[];
  selectedGenerations?: string[];
}

/** Current mobile.bg ads for this model, best deals first. Hidden when no listings API is configured. */
export function LiveListings({
  make,
  model,
  yearPrices,
  fallbackImageUrl,
  generationFilters = [],
  selectedGenerations = [],
}: LiveListingsProps) {
  const { data, isLoading, isError } = useLiveListings(make, model);
  const [showAll, setShowAll] = useState(false);
  const allRanked = useMemo(() => (data ? rankListingsByDeal(data.listings, yearPrices) : []), [data, yearPrices]);
  const selectedRanges = generationFilters
    .filter(({ generation, years }) => selectedGenerations.includes(generation) && years !== null)
    .map(({ years }) => years as YearRange);
  const ranked = allRanked.filter(({ listing }) => listingInYearRanges(listing, selectedRanges));
  const hasVerdicts = ranked.some(({ verdict }) => verdict !== null);

  if (!LISTINGS_API_URL) {
    return null;
  }

  const visible = showAll ? ranked : ranked.slice(0, INITIAL_VISIBLE);

  return (
    <Card sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="h6">For sale now on mobile.bg</Typography>
        {data && (
          <Link href={data.sourceUrl} target="_blank" rel="noopener noreferrer" underline="hover" variant="body2">
            See all on mobile.bg <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
          </Link>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {data ? `Checked ${minutesAgo(data.fetchedAt)}. ` : ''}
        {hasVerdicts
          ? 'Best deals first: each price is compared with the median for cars of the same year.'
          : 'Cheapest first. Deal badges show once we have enough ads per year to compare against.'}
      </Typography>

      {isLoading && [0, 1, 2].map((i) => <Skeleton key={i} variant="text" height={56} />)}

      {isError && <Alert severity="info">Live listings are unavailable right now. Please try again later.</Alert>}

      {data?.stale && (
        <Alert severity="warning" sx={{ my: 1 }}>
          mobile.bg didn&apos;t respond, so these listings are from {minutesAgo(data.fetchedAt)} and some may be gone.
        </Alert>
      )}

      {data && allRanked.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No listings with a price right now.
        </Typography>
      )}

      {data && allRanked.length > 0 && ranked.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          None of the {allRanked.length} newest ads are from these generations.{' '}
          <Link href={data.sourceUrl} target="_blank" rel="noopener noreferrer" underline="hover">
            Search all of them on mobile.bg
          </Link>
          .
        </Typography>
      )}

      {selectedGenerations.length > 0 && ranked.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5 }}>
          {ranked.length} of {allRanked.length} ads match by production year.
        </Typography>
      )}

      {visible.map(({ listing, verdict }, index) => (
        <Box key={listing.url}>
          {index > 0 && <Divider />}
          <ListingRow listing={listing} verdict={verdict} fallbackImageUrl={fallbackImageUrl} />
        </Box>
      ))}

      {!showAll && ranked.length > INITIAL_VISIBLE && (
        <Button onClick={() => setShowAll(true)} sx={{ mt: 1 }}>
          Show all {ranked.length}
        </Button>
      )}
    </Card>
  );
}
